/**
 * Turns the authoritative snapshot stream into positional sound events, the audio
 * sibling of the renderer's per-viewer roof-hiding and overhead line-of-sight: a
 * render-time reaction each client computes for itself off the same read-model
 * world, never anything on the wire (no protocol/sim change - see ADR-0092).
 *
 * Two things make sound here, both *derived* from comparing successive world
 * states - exactly what ClassicUO's client does locally rather than being told to
 * by the server:
 *
 *  - **Footsteps**: a mobile whose resting tile changed since the last snapshot
 *    took a step. Like `Mobile.ProcessFootstepsSound`, each mobile plays an
 *    *alternating* `footstep-a`/`footstep-b` and is rate-limited to one footstep
 *    per {@link SoundConfig.footstepCooldownMs} (~520ms), so a run (a step every
 *    ~100ms) doesn't machine-gun - the cadence is the same whether walking or
 *    running, matching UO.
 *  - **Doors**: a door entity whose `open` flipped since the last snapshot plays
 *    `door-open` or `door-close`.
 *
 * Each event carries a volume already scaled by the tile distance from the local
 * player ({@link proximityVolume}), so the caller just plays it. The observer is
 * the *decision*; {@link AudioManager} is the *playback*. It holds only small
 * per-entity tracking maps (last tile, last-footstep time, the a/b toggle, last
 * door state) and reads no Pixi/DOM/audio API, so it is unit tested directly by
 * feeding it successive worlds and timestamps. The render clock (`performance.now`)
 * is passed in as `nowMs`, the same way the renderer takes its frame dt - the sim
 * stays clock-free; this is a client render concern.
 */
import type { World } from '../sim/world';
import type { EntityId } from '../sim/entity';
import { proximityVolume, tileDistance, type Tile } from './proximity';
import { isVortexBody, isMonsterBody, type MonsterSoundId, type ProjectileEffectId } from '../sim/content';
import { vortexCreatureCue, monsterCue, spellCue } from './combatCue';
import { projectileRemovalWasContact } from '../sim/projectile';

/**
 * The baked sound effects, keyed by the {@link sounds} content registry ids. Most
 * are positional (footsteps, doors) and decided by the {@link SoundObserver};
 * `level-up` is a non-positional UI cue played directly on the cosmetic-award emote
 * (PRD #101), and `sword7` likewise on any player line containing "bank" (ADR-0078),
 * neither derived from the snapshot. The `ugh-*` grunts and `rat-die-*` squeals are
 * the Rat Swarm combat cues, fired off the one-shot action-animation broadcast (see
 * {@link combatCue}) rather than the snapshot. `swarm-start`/`swarm-over` are the
 * event-boundary cues, fired off the RatSwarmStatus active<->inactive transition
 * (see {@link swarmCue}). The `hit-*`/`whiff-*`/`death-*`/`resurrect` cues are the
 * duel combat sounds (PRD #252, ADR-0131), fired off the duel's action-animation
 * broadcasts and combat-text / resurrection signals (see {@link combatCue}).
 * `storm-start` is Blackthorn's Storm event-boundary growl, fired off the
 * StormStatus active transition (see {@link ./stormCue}); `vortex-attack-1..3` is
 * the randomized wind gust an energy vortex's swing sounds, proximity-scaled off
 * the vortex's attack action-animation broadcast (Slice E, ADR-0152) - and, now, off
 * the vortex's snapshot-derived *movement* too (this observer, ADR-0162), so the wind
 * is heard as vortexes roam, not only on a strike. `llama-1/2/4/5` are the chaos-llama
 * variant's barks (ADR-0162), the same movement + attack cues for a llama-bodied vortex.
 */
export type SoundId =
  | 'footstep-a'
  | 'footstep-b'
  | 'door-open'
  | 'door-close'
  | 'level-up'
  | 'sword7'
  | 'card-duel-start'
  | 'card-your-turn'
  | 'ugh-m-1'
  | 'ugh-m-2'
  | 'ugh-m-3'
  | 'ugh-m-4'
  | 'ugh-m-5'
  | 'ugh-f-1'
  | 'ugh-f-2'
  | 'ugh-f-3'
  | 'rat-die-1'
  | 'rat-die-2'
  | 'rat-die-3'
  | 'swarm-start'
  | 'swarm-over'
  | 'storm-start'
  | 'vortex-attack-1'
  | 'vortex-attack-2'
  | 'vortex-attack-3'
  | 'llama-1'
  | 'llama-2'
  | 'llama-4'
  | 'llama-5'
  | 'hit-m-1'
  | 'hit-m-2'
  | 'hit-m-3'
  | 'hit-f-1'
  | 'hit-f-2'
  | 'hit-f-3'
  | 'whiff-1'
  | 'whiff-2'
  | 'whiff-3'
  | 'death-m-1'
  | 'death-m-2'
  | 'death-f-1'
  | 'death-f-2'
  | 'resurrect'
  // The arena fire-mage spell FX sounds (ADR-0187), one per baked effect, played
  // at the volley's flight/impact instants (see combatCue.spellCue).
  | 'spell-magic-arrow'
  | 'spell-fireball'
  | 'spell-explosion'
  | 'spell-flame-strike'
  | 'spell-poison'
  | 'spell-lightning'
  | 'spell-greater-heal'
  | 'spell-cure'
  | 'warrior-bless'
  | 'sphere-fizzle'
  // The arena archer's shot cues (PRD #446 Slice 1, ADR-0237): a bow twang / a
  // crossbow release (off the flight FX, spellCue path), and the arrow/bolt
  // impact thwack (off the onHit viaEffect seam, on the struck enemy).
  | 'bow-shot'
  | 'crossbow-shot'
  | 'arrow-hit'
  // The moongate cues (PRD #362 Slice A), both derived from the snapshot by this
  // observer: `moongate-open` when a gate entity appears (the summon hum),
  // `moongate-enter` when a mobile teleport-jumps away from beside a gate (the
  // traveller's whoosh - full volume for the traveller, proximity-scaled at the
  // gate for bystanders).
  | 'moongate-open'
  | 'moongate-enter'
  // The King's Justice cues (PRD #361, ADR-0194): the smoke-poof teleport sound on
  // every guard-ritual poof-in/out (fired off the JusticeFx broadcast), and the
  // halberd's polearm hit on the guard's `attack-2h` wack action-animation.
  | 'poof'
  | 'halberd-hit'
  // The arena XP-gem absorb plink (VS-style XP orbs): played client-side as a
  // magnetized orb cluster finishes its suck-in to the runner.
  | 'xp-pickup'
  // Positive collection cue after an outfit-slot purchase is confirmed.
  | 'collection-positive'
  // The arena Vacuum grab whoosh (PRD #431 Slice C): one full-volume cue as the
  // runner claims a vacuum pickup and the whole gem field inrushes.
  | 'vacuum-grab'
  // The arena level-up fanfare's cap note (ADR-0218): UO sfx15, sounded as the
  // plink arpeggio resolves when the draft opens.
  | 'level-up-fanfare'
  // The arena low-HP heartbeat (PRD #430 Slice D, ADR-0227): the throttled
  // lub-dub while the runner is under the danger threshold, volume ramping as
  // HP falls.
  | 'heartbeat'
  // The bestiary monster cues (ADR-0187): `mon-<family>-<role>` for every sound
  // family x move/attack/hurt/die, generated alongside the monsters registry.
  | MonsterSoundId;

/** A sound to play, with its proximity-scaled volume already resolved to `[0, 1]`. */
export interface SoundEvent {
  sound: SoundId;
  volume: number;
  /**
   * Optional playback-rate multiplier (1 = as recorded). Rapid-fire cues (the
   * arena XP-pickup plink) pass a slight per-event variance so a burst of the
   * same sample sparkles instead of machine-gunning; absent everywhere else,
   * so the positional world sounds always play as recorded.
   */
  rate?: number;
}

/** Tunables for the positional-sound model; {@link DEFAULT_SOUND_CONFIG} is faithful to UO. */
export interface SoundConfig {
  /** Audible radius in tiles (ClassicUO client view range ~18); beyond it, silent. */
  maxTiles: number;
  /** Minimum ms between footstep sounds for one mobile (ClassicUO's ~520ms). */
  footstepCooldownMs: number;
  /** Footstep loudness ceiling `[0,1]` (volume at distance 0). Subtler than doors. */
  footstepVolume: number;
  /** Door open/close loudness ceiling `[0,1]` (volume at distance 0). */
  doorVolume: number;
}

/**
 * UO-faithful defaults: an 18-tile audible radius (the common client view range),
 * the ~520ms footstep cadence ClassicUO derives from `400 * 13/10`, footsteps
 * mixed below doors so the room's ambient shuffle never drowns the punctuating
 * clack of a door.
 */
export const DEFAULT_SOUND_CONFIG: SoundConfig = {
  maxTiles: 18,
  footstepCooldownMs: 520,
  footstepVolume: 0.5,
  doorVolume: 0.9,
};

/**
 * Minimum ms between a vortex-kind creature's **roam** wind/bark for one creature
 * (ADR-0162). A vortex crosses a tile in ~140ms (~70ms for a llama), so an unthrottled
 * per-step cue would machine-gun; ~700ms lets the wind be heard *often* as it hunts
 * without spamming every tile - a touch slower than a footstep so the ambience reads as
 * gusts, not chatter.
 */
export const VORTEX_MOVE_COOLDOWN_MS = 700;

/**
 * Minimum ms between one bestiary monster's **roam** voice attempts (ADR-0187), the
 * vortex-roam throttle's sibling. An arena wave is *many* creatures at once (the
 * vortex cap was 11; waves run bigger), so per-creature it is throttled far slower
 * than the vortex wind AND each expiry only *rolls* to speak ({@link
 * MONSTER_MOVE_CHANCE}) - UO creatures mutter sporadically, not metronomically. The
 * cooldown stamps on the roll, fire or not, so a creature's expected voice interval
 * is cooldown / chance (~7s), and a surrounding wave reads as an ambient chorus
 * rather than a klaxon wall.
 */
export const MONSTER_MOVE_COOLDOWN_MS = 1800;

/** Probability a monster's expired roam cooldown actually voices this step (ADR-0187). */
export const MONSTER_MOVE_CHANCE = 0.25;

/**
 * Pack size above which the per-monster roam-voice chance scales *down* so the
 * *aggregate* horde murmur plateaus instead of climbing linearly with the pack.
 * The per-creature murmur cost is otherwise `count / (cooldown/chance)` voices a
 * second, so a big arena wave (dozens of creatures at once) roars where a dozen
 * murmured. Above this threshold the chance is multiplied by `softcap / count`,
 * holding the room's total roam voices to roughly what {@link
 * MONSTER_MURMUR_SOFTCAP} creatures make - still a many-throated horde, not a
 * per-creature klaxon wall. At or below it the chance is untouched, so small and
 * medium packs sound exactly as before. Combat swings/deaths ride their own
 * global cooldowns in `main.ts` and are unaffected.
 */
export const MONSTER_MURMUR_SOFTCAP = 12;

/** Loudness ceiling `[0,1]` for the moongate summon hum at distance 0 (PRD #362). */
export const MOONGATE_OPEN_VOLUME = 0.9;

/** Loudness ceiling `[0,1]` for the moongate travel whoosh (PRD #362). */
export const MOONGATE_ENTER_VOLUME = 0.8;

/**
 * How close (Chebyshev tiles) a teleport-jumping mobile's *departure* tile must be
 * to a live moongate for the jump to read as "entered that gate" and sound the
 * travel whoosh. `MOONGATE_ENTER_RANGE` is 2, but an accepted entry teleports from
 * wherever the player stood inside it, so the whole reach counts.
 */
const MOONGATE_JUMP_NEAR_TILES = 2;

/** Loudness ceiling `[0,1]` for a monster's roam voice, below its attack/die cues. */
export const MONSTER_MOVE_VOLUME = 0.4;

/**
 * Loudness ceiling `[0,1]` for a vortex's **roam** wind/bark at distance 0 (ADR-0162),
 * mixed a little below its attack gust (`VORTEX_ATTACK_VOLUME` in `main.ts`) so a strike
 * still punctuates the ambient roam, and proximity-scaled per creature like a footstep.
 */
export const VORTEX_MOVE_VOLUME = 0.45;

/**
 * Loudness ceiling `[0,1]` for an arena projectile's **impact** thump at distance
 * 0 (PRD #399 Slice D), proximity-scaled from the strike tile like a footstep. A
 * basic straight shot's whole audible life is this one cue on contact - it plays
 * the projectile look's own classic spell sound ({@link spellCue}: `magic-arrow`
 * -> `spell-magic-arrow`, `fireball` -> `spell-fireball`) at the moment the bolt
 * is removed from the snapshot.
 */
export const PROJECTILE_IMPACT_VOLUME = 0.6;

/**
 * Minimum ms between projectile-impact thumps across the whole observer (PRD #399
 * Slice D). A multi-shot / pierce volley can remove several bolts in one snapshot
 * delta; without a throttle a wide wall of fire landing together would machine-gun
 * the spell sound. One impact per this window keeps the layer punchy - the
 * SPELL_FX_COOLDOWN throttle the old resolved cascade used, moved to the removal
 * seam. Stamped only when an impact actually fires.
 */
export const PROJECTILE_IMPACT_COOLDOWN_MS = 120;

/** Last-seen state of a live projectile, for deriving its impact cue on removal. */
interface ProjectileTrack {
  tile: Tile;
  /** Last replicated ttl - the contact-vs-expiry discriminator (see {@link projectileRemovalWasContact}). */
  ttlTicks: number;
  look: ProjectileEffectId;
}

interface MobileTrack {
  tile: Tile;
  /** Last time (ms) a footstep actually fired, for the per-mobile cooldown. */
  lastFootstepMs: number;
  /** 0 -> next footstep is `-a`, 1 -> `-b`; flips each time one fires (UO alternation). */
  toggle: 0 | 1;
}

export class SoundObserver {
  private readonly mobiles = new Map<EntityId, MobileTrack>();
  private readonly doors = new Map<EntityId, boolean>();
  /** Live moongate cells by entity id (PRD #362); gates never move once open. */
  private readonly moongates = new Map<EntityId, Tile>();
  /**
   * Last-seen state of every live arena projectile (PRD #399 Slice D). A bolt
   * that leaves this set between snapshots was removed by {@link stepProjectiles}
   * - a contact (impact thump) or a max-range expiry (silent) - which the tracked
   * `ttlTicks` distinguishes ({@link projectileRemovalWasContact}). The impact is
   * derived purely from the snapshot diff (the moongate-removal precedent), so no
   * new wire message carries it.
   */
  private readonly projectiles = new Map<EntityId, ProjectileTrack>();
  /** Last time (ms) a projectile-impact thump fired, for {@link PROJECTILE_IMPACT_COOLDOWN_MS}. */
  private lastProjectileImpactMs = -Infinity;
  /**
   * Bestiary-monster body count from the *previous* {@link observe} pass, driving
   * this pass's roam-murmur throttle ({@link MONSTER_MURMUR_SOFTCAP}). Read from
   * last frame (not recomputed mid-pass) since pack size barely moves frame to
   * frame - a one-frame lag on a fresh arrival is inaudible, and it avoids a
   * second full sweep of the world.
   */
  private lastMonsterCount = 0;
  /**
   * Whether at least one {@link observe} pass has completed. The per-entity
   * "first sighting is silent" rule can't announce a moongate that *spawns*
   * mid-session (a spawn IS its first sighting), so gate arrival is keyed off
   * this observer-level flag instead: silent on the joining pass (a gate already
   * standing when you log in makes no noise), a hum on any gate that appears
   * after it.
   */
  private primed = false;

  /**
   * `rng` seeds the random pick of a vortex-kind creature's roam wind/bark cue (ADR-0162),
   * defaulting to `Math.random`; a test injects a scripted source to pin the selection. It
   * is unused by the deterministic footstep/door paths, so pre-vortex construction is
   * unchanged.
   */
  constructor(
    private readonly config: SoundConfig = DEFAULT_SOUND_CONFIG,
    private readonly rng: () => number = Math.random,
  ) {}

  /**
   * Compare this snapshot's world against the last and return the sounds to play.
   * `localId` is the listener (the local player) the volumes are measured from;
   * `nowMs` is the render clock for the footstep cooldown. A first sighting of an
   * entity is only *recorded* (no sound), so joining a populated bank - or seeing a
   * door already open - doesn't erupt in footsteps and slams; sound fires only on a
   * subsequent *change*.
   */
  observe(world: World, localId: EntityId | undefined, nowMs: number): SoundEvent[] {
    const events: SoundEvent[] = [];
    const listener = this.listenerTile(world, localId);

    const liveMobiles = new Set<EntityId>();
    const liveDoors = new Set<EntityId>();
    const liveMoongates = new Set<EntityId>();
    const liveProjectiles = new Set<EntityId>();
    // This pass's bestiary-monster count, stamped at the end to throttle *next*
    // pass's murmur (see MONSTER_MURMUR_SOFTCAP / lastMonsterCount).
    let monsterCount = 0;
    // Roll the murmur gate against a pack-scaled chance: at/under the softcap this
    // is the bare MONSTER_MOVE_CHANCE, above it the per-creature chance falls as
    // 1/count so the room's total roam voices plateau rather than climb.
    const murmurChance =
      MONSTER_MOVE_CHANCE * Math.min(1, MONSTER_MURMUR_SOFTCAP / Math.max(1, this.lastMonsterCount));

    for (const entity of world.all()) {
      if (entity.mobile) {
        liveMobiles.add(entity.id);
        const tile: Tile = { x: entity.mobile.tileX, y: entity.mobile.tileY };
        const track = this.mobiles.get(entity.id);
        if (!track) {
          // First sighting: record position, stay silent.
          this.mobiles.set(entity.id, { tile, lastFootstepMs: -Infinity, toggle: 0 });
        } else {
          const prevTile = track.tile;
          const moved = tile.x !== track.tile.x || tile.y !== track.tile.y;
          track.tile = tile;
          // A multi-tile jump is a teleport, not a step. One that departs from
          // beside a live moongate is a gate entry (PRD #362): sound the travel
          // whoosh - at full ceiling for the traveller themselves (they are now
          // far from the gate, but it fired *on* them), proximity-scaled at the
          // gate's cell for bystanders. The gate positions are last pass's (the
          // sweep below runs after this loop), so a gate consumed in the same
          // delta as the jump still sounds its own entry.
          const jumped = Math.max(Math.abs(tile.x - prevTile.x), Math.abs(tile.y - prevTile.y)) > 1;
          if (jumped && listener) {
            for (const gate of this.moongates.values()) {
              const near =
                Math.max(Math.abs(prevTile.x - gate.x), Math.abs(prevTile.y - gate.y)) <=
                MOONGATE_JUMP_NEAR_TILES;
              if (!near) continue;
              const volume =
                entity.id === localId
                  ? MOONGATE_ENTER_VOLUME
                  : proximityVolume(
                      tileDistance(gate, listener),
                      this.config.maxTiles,
                      MOONGATE_ENTER_VOLUME,
                    );
              if (volume > 0) events.push({ sound: 'moongate-enter', volume });
              break;
            }
          }
          // A vortex-kind creature (energy vortex or chaos llama, ADR-0162) glides rather
          // than walks: as it roams it sounds its wind/bark cue instead of a footstep, so
          // the storm's wind is heard *throughout* the fight, not only on a strike (the
          // Slice E attack cue). Throttled to its own slower cadence
          // ({@link VORTEX_MOVE_COOLDOWN_MS}) so a ~140ms-gait vortex does not machine-gun,
          // and proximity-scaled per creature like a footstep; the random set-pick is the
          // shared {@link vortexCreatureCue} (wind for a vortex, bark for a llama).
          const vortexKind = isVortexBody(entity.renderable?.body);
          // A bestiary monster (ADR-0187) likewise voices its move/idle sound as it
          // roams instead of a footstep, throttled slower and probability-gated (see
          // {@link MONSTER_MOVE_COOLDOWN_MS}) so an arena wave murmurs, not roars.
          const monsterKind = !vortexKind && isMonsterBody(entity.renderable?.body);
          if (monsterKind) monsterCount += 1; // pack size for next pass's murmur throttle
          const cooldown = vortexKind
            ? VORTEX_MOVE_COOLDOWN_MS
            : monsterKind
              ? MONSTER_MOVE_COOLDOWN_MS
              : this.config.footstepCooldownMs;
          // A step plays at most once per cooldown. The toggle/cooldown advance
          // whenever a step clears the cooldown - even if the listener is out of
          // earshot (volume 0) - so the a/b alternation and cadence stay in step
          // with UO regardless of how far away the walker is (matches ClassicUO,
          // which still stamps LastStepSoundTime at zero volume).
          // A jump is a teleport, never a step: it makes no footstep (and no roam
          // wind) - its only voice is the moongate whoosh above, when one applies.
          if (moved && !jumped && listener && nowMs - track.lastFootstepMs >= cooldown) {
            if (vortexKind) {
              const volume = proximityVolume(
                tileDistance(tile, listener),
                this.config.maxTiles,
                VORTEX_MOVE_VOLUME,
              );
              const cue = vortexCreatureCue(entity.renderable?.body, volume, this.rng);
              if (cue) events.push(cue);
            } else if (monsterKind) {
              // The roll stamps the cooldown either way (below): each expiry is one
              // chance to mutter, so the expected interval is cooldown / chance -
              // and the chance is thinned for a big pack so the horde murmur
              // plateaus instead of roaring (MONSTER_MURMUR_SOFTCAP).
              if (this.rng() < murmurChance) {
                const volume = proximityVolume(
                  tileDistance(tile, listener),
                  this.config.maxTiles,
                  MONSTER_MOVE_VOLUME,
                );
                const cue = monsterCue(entity.renderable?.body, 'move', volume);
                if (cue) events.push(cue);
              }
            } else {
              const volume = proximityVolume(
                tileDistance(tile, listener),
                this.config.maxTiles,
                this.config.footstepVolume,
              );
              if (volume > 0) {
                events.push({ sound: track.toggle === 0 ? 'footstep-a' : 'footstep-b', volume });
              }
              track.toggle = track.toggle === 0 ? 1 : 0;
            }
            track.lastFootstepMs = nowMs;
          }
        }
      }

      if (entity.door && entity.position) {
        liveDoors.add(entity.id);
        const open = entity.door.open;
        const prev = this.doors.get(entity.id);
        this.doors.set(entity.id, open);
        if (prev !== undefined && prev !== open && listener) {
          const volume = proximityVolume(
            tileDistance({ x: entity.position.x, y: entity.position.y }, listener),
            this.config.maxTiles,
            this.config.doorVolume,
          );
          if (volume > 0) events.push({ sound: open ? 'door-open' : 'door-close', volume });
        }
      }

      if (entity.projectile && entity.position) {
        // Just record the live bolt's latest tile, ttl, and look (a first sighting
        // is silent, like every other tracked entity). The impact cue is derived
        // when it LEAVES the set - see the removal sweep below.
        liveProjectiles.add(entity.id);
        this.projectiles.set(entity.id, {
          tile: { x: entity.position.x, y: entity.position.y },
          ttlTicks: entity.projectile.ttlTicks,
          look: entity.projectile.look,
        });
      }

      if (entity.moongate && entity.position) {
        liveMoongates.add(entity.id);
        if (!this.moongates.has(entity.id)) {
          const gateTile: Tile = { x: entity.position.x, y: entity.position.y };
          this.moongates.set(entity.id, gateTile);
          // A gate that appears after the joining pass just tore open: the summon
          // hum, proximity-scaled from its cell (PRD #362). A gate already standing
          // at login stays silent (the primed gate - see the field doc).
          if (this.primed && listener) {
            const volume = proximityVolume(
              tileDistance(gateTile, listener),
              this.config.maxTiles,
              MOONGATE_OPEN_VOLUME,
            );
            if (volume > 0) events.push({ sound: 'moongate-open', volume });
          }
        }
      }
    }

    // Forget entities that have left, so a later rejoin is a fresh "first sighting"
    // (no spurious footstep from a stale tile, no phantom door toggle).
    for (const id of this.mobiles.keys()) if (!liveMobiles.has(id)) this.mobiles.delete(id);
    for (const id of this.doors.keys()) if (!liveDoors.has(id)) this.doors.delete(id);
    for (const id of this.moongates.keys()) if (!liveMoongates.has(id)) this.moongates.delete(id);

    // A projectile that left the snapshot was removed by stepProjectiles (PRD #399
    // Slice D): a CONTACT sounds its look's spell thump (the enemy's own damage
    // float carries the rest), a max-range EXPIRY dissipates silently. The whole
    // decision is snapshot-derived - the moongate-removal precedent - so no wire
    // message carries it. Throttled across the observer so a wide volley landing
    // together does not machine-gun.
    for (const [id, track] of this.projectiles) {
      if (liveProjectiles.has(id)) continue;
      this.projectiles.delete(id);
      if (!listener || !projectileRemovalWasContact(track.ttlTicks)) continue;
      if (nowMs - this.lastProjectileImpactMs < PROJECTILE_IMPACT_COOLDOWN_MS) continue;
      const volume = proximityVolume(
        tileDistance(track.tile, listener),
        this.config.maxTiles,
        PROJECTILE_IMPACT_VOLUME,
      );
      const cue = spellCue(track.look, volume);
      if (cue) {
        this.lastProjectileImpactMs = nowMs;
        events.push(cue);
      }
    }

    this.lastMonsterCount = monsterCount; // throttle next pass's murmur to this pack
    this.primed = true;
    return events;
  }

  /**
   * Drop all per-entity tracking so the next {@link observe} is a fresh "first
   * sighting" pass (record positions, stay silent). Needed at the Arena's
   * mid-session world crossing (ADR-0177): entity ids **overlap across worlds**
   * (the run allocator is pre-seated at the session's id, ADR-0176), so a reused
   * id at a completely different tile would otherwise read as a "step" and fire a
   * spurious proximity footstep - the same reason a reconnect builds a fresh
   * observer per connection.
   */
  reset(): void {
    this.mobiles.clear();
    this.doors.clear();
    this.moongates.clear();
    this.projectiles.clear();
    this.lastProjectileImpactMs = -Infinity;
    this.lastMonsterCount = 0;
    this.primed = false;
  }

  /** The local player's current tile, or undefined when it is not (yet) in the world. */
  private listenerTile(world: World, localId: EntityId | undefined): Tile | undefined {
    if (localId === undefined) return undefined;
    const mobile = world.get(localId)?.mobile;
    return mobile ? { x: mobile.tileX, y: mobile.tileY } : undefined;
  }
}
